import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { LoadingButton } from '@mui/lab';
import { AppBar, Button, Stack, Toolbar } from '@mui/material';

import { TP_COLORS } from '../../../constants';
import { getAuthState } from '../../../store/AuthSlice';
import { ISubTopic, getGroupedSubTopics } from '../../../store/SubTopicSlice';
import { getQuizById, getQuizState } from '../../../store/QuizSlice';
import {
  createNewSubmissionByQuizId,
  getSubmissionsForQuizHistory,
  getSubmissionState,
  ISubmission,
  loadMoreSubmissionsForQuizHistory,
} from '../../../store/SubmissionSlice';
import { useAppSelector } from '../../../hooks';
import { checkUserPermission, getUrlConvertedString } from '../../../helper';
import MobileHeader from '../../../components/Mobile/Header';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import SubmissionListItem from './components/SubmissionListItem';
import TopicHeader from '../../../components/Headers/TopicHeader';
import { getMetaTagState } from '../../../store/MetaTagSlice';

const intialState = {
  filterData: {
    limit: 30,
    offset: 0,
  },
};

const SubmissionHistoryForQuiz: React.FC = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Store
  const quizState = useAppSelector(getQuizState);
  const authState = useAppSelector(getAuthState);
  const submissionState = useAppSelector(getSubmissionState);
  const metaTagState = useAppSelector(getMetaTagState);

  // Permissions
  const createSubmissionPermission = checkUserPermission(authState.user, {
    submission: ['create'],
  });

  // State
  const [state, setState] = useState(intialState);

  const quizData = quizState.quiz;
  const quizTopicData = quizData && quizData.topic ? quizData.topic : undefined;
  const urlStringPattern = params.pattern || '';
  const urlStringPatternDivided = urlStringPattern.split('_').reverse();
  const subTopicId = urlStringPatternDivided[0] || '';

  let quizSubTopicData: ISubTopic | undefined;

  if (quizData && quizData.subTopics && quizData.subTopics.length) {
    const quizSubTopicItem = quizData.subTopics.find(
      (item) => item.subTopic?._id === subTopicId
    );

    if (quizSubTopicItem) {
      quizSubTopicData = quizSubTopicItem.subTopic;
    }
  }

  const hasResources =
    quizSubTopicData &&
    quizSubTopicData.resources &&
    quizSubTopicData.resources.length;

  let curriculum = '';
  let urlConvertedCurriculum = '';
  let subject = '';
  let urlConvertedSubject = '';
  let topic = '';
  let subTopic = '';
  let urlConvertedSubTopic = '';
  let metaTitle = '';
  let metaDesc =
    'We have a wide range of resources to help you prepare for your';

  if (quizTopicData) {
    subject = quizTopicData.subject;
    topic = quizTopicData.name;
    curriculum = quizTopicData.curriculum;
    urlConvertedCurriculum = getUrlConvertedString(curriculum);
    urlConvertedSubject = getUrlConvertedString(subject);

    metaTitle += `${curriculum}`;
    metaDesc += ` ${curriculum}`;
    metaDesc += ' exams. Start practicing our';

    metaTitle += ` | ${subject}`;
    metaDesc += ` ${subject}`;
  }

  if (quizSubTopicData) {
    subTopic = quizSubTopicData.name;
    urlConvertedSubTopic = getUrlConvertedString(subTopic);

    metaTitle += ` | ${subTopic}`;
    metaDesc += ` ${subTopic}`;

    metaTitle += ' | Tutopiya';
    metaDesc += ' questions today!';
  }

  useEffect(() => {
    dispatch(getGroupedSubTopics({ subTopicId }));

    if (subTopicId) {
      dispatch(getQuizById(params.quizId || ''));
    }

    dispatch(
      getSubmissionsForQuizHistory({
        ...state.filterData,
        quizId: params.quizId || '',
      })
    );
  }, [subTopicId]);

  const startQuiz = () => {
    if (quizData) {
      dispatch(
        createNewSubmissionByQuizId({
          subTopicId,
          quizId: quizData._id || '',
        })
      );
    }
  };

  const handleLoadMore = () => {
    const newOffset = submissionState.list.length;

    if (params.quizId) {
      dispatch(
        loadMoreSubmissionsForQuizHistory({
          ...state.filterData,
          quizId: params.quizId,
        })
      );

      setState((prevState) => ({
        ...prevState,
        filterData: {
          ...prevState.filterData,
          offset: newOffset,
        },
      }));
    }
  };

  const onFindResources = () => {
    navigate(
      `${process.env.PUBLIC_URL}/dashboard/my-quizzes/subtopic/${urlConvertedCurriculum}_${urlConvertedSubject}_${urlConvertedSubTopic}_${subTopicId}`
    );
  };

  const onSubmissionClicked = (submission: ISubmission) => {
    if (submission.status === 'pending') {
      navigate(
        `${process.env.PUBLIC_URL}/dashboard/my-quizzes/subtopic/${subTopicId}/${submission._id}/${urlConvertedCurriculum}_${urlConvertedSubject}_${urlConvertedSubTopic}_${subTopicId}`
      );
    } else {
      navigate(
        `${process.env.PUBLIC_URL}/dashboard/quiz-history/questions/${submission._id}/${urlConvertedCurriculum}_${urlConvertedSubject}_${urlConvertedSubTopic}_${subTopicId}`
      );
    }
  };

  return (
    <div>
      {!metaTagState.metaTags ||
        (!metaTagState.metaTags[location.pathname.replace(/[/]$/, '')] && (
          <Helmet>
            <title>{metaTitle}</title>
            <meta name="description" content={metaDesc} />
          </Helmet>
        ))}
      <MobileHeader
        leftContents={{
          type: 'subtopic-nav-bar',
        }}
      />
      <TopicHeader subject={subject} subTopics={[subTopic]} topic={topic} />
      {quizState.status === 'loading' ? (
        <div style={{ padding: '30px 0' }}>
          <BasicLoader />
        </div>
      ) : (
        <>
          {hasResources ? (
            <AppBar
              position="static"
              color="secondary"
              style={{
                background: `url('${process.env.PUBLIC_URL}/images/resourceLib/app-bar-bg.png') center right/contain no-repeat, #2C2C2C`,
              }}
            >
              <Toolbar>
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  onClick={onFindResources}
                  style={{
                    color: TP_COLORS.black,
                    backgroundColor: TP_COLORS.white,
                    minWidth: '100px',
                  }}
                >
                  Find Resources
                </Button>
              </Toolbar>
            </AppBar>
          ) : null}
          <div style={{ padding: '15px' }}>
            {createSubmissionPermission && (
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                sx={{ mb: '30px' }}
              >
                {createSubmissionPermission && (
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    loading={submissionState.status === 'loading'}
                    onClick={startQuiz}
                  >
                    Start a Quiz
                  </LoadingButton>
                )}
              </Stack>
            )}
            <div style={{ marginBottom: '30px' }}>
              {submissionState.list.map((submission, index) => (
                <SubmissionListItem
                  key={index}
                  attempt={index + 1}
                  submission={submission}
                  onViewClicked={() => onSubmissionClicked(submission)}
                />
              ))}
              {submissionState.count > submissionState.list.length && (
                <Stack
                  direction="row"
                  justifyContent="center"
                  style={{ marginBottom: '30px' }}
                >
                  <LoadingButton
                    loading={submissionState.status === 'loading'}
                    onClick={handleLoadMore}
                  >
                    Load More
                  </LoadingButton>
                </Stack>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubmissionHistoryForQuiz;
